import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Table, Button, Form, Modal, Row, Col, Badge, InputGroup, Spinner, Alert, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FaEdit, FaTrash, FaSearch, FaPlus, FaUsers, FaCheck, FaArrowLeft, FaUserPlus, FaUndo, FaEnvelope, FaTimes, FaLayerGroup } from 'react-icons/fa';
import { toast } from 'react-toastify';
import api from '../../../api/apiClient';
import { validateNavigationToken } from '../../../utils/urlSecurity';

const CourseEnrollmentsPage = () => {
  const { courseId: secureToken } = useParams();
  const navigate = useNavigate();
  const [courseId, setCourseId] = useState(null);
  const [tokenValid, setTokenValid] = useState(false);
  
  const [enrollments, setEnrollments] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedEnrollment, setSelectedEnrollment] = useState(null);
  const [enrollmentToDelete, setEnrollmentToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  const [enrollmentForm, setEnrollmentForm] = useState({
    user_id: '',
    enrollment_date: new Date().toISOString().split('T')[0],
    status: 1
  });

  // Group-enroll state
  const [showGroupEnrollModal, setShowGroupEnrollModal] = useState(false);
  const [platformGroups, setPlatformGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [groupMembers, setGroupMembers] = useState({ assigned: [], skipped: [] });
  const [groupMembersLoading, setGroupMembersLoading] = useState(false);
  const [groupEnrollSaving, setGroupEnrollSaving] = useState(false);

  const validateToken = () => {
    const validation = validateNavigationToken(secureToken);
    
    if (!validation.valid) {
      if (validation.expired) {
        toast.error('Navigation token has expired. Please navigate from the courses page again.');
      } else {
        toast.error('Invalid navigation token. Please navigate from the courses page.');
      }
      navigate('/admin/courses');
      return false;
    }
    
    setCourseId(validation.courseId);
    setTokenValid(true);
    return true;
  };

  useEffect(() => {
    if (!validateToken()) {
      return;
    }
  }, [secureToken]);

  useEffect(() => {
    if (tokenValid && courseId) {
      fetchCourseDetails();
      fetchEnrollments();
    }
  }, [courseId, tokenValid]);

  const fetchCourseDetails = async () => {
    try {
      const response = await api.courses.getById(courseId);
      if (response.data.success) {
        setCourse(response.data.data);
      } else {
        setCourse(response.data);
      }
    } catch (error) {
      console.error('Error fetching course details:', error);
      toast.error('Failed to fetch course details');
    }
  };

  const fetchEnrollments = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/enrollments/course/${courseId}`, {
        params: { includeDeleted: 'true' }
      });
      
      if (response.data.success) {
        setEnrollments(response.data.data || []);
      } else {
        setEnrollments(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching enrollments:', error);
      toast.error('Failed to fetch enrollments');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableUsers = async () => {
    if (!courseId) {
      console.error('Course ID not available for fetching users');
      toast.error('Course information not loaded yet. Please try again.');
      return;
    }
    
    try {
      const response = await api.get(`/enrollments/course/${courseId}/available-users`);
      setAvailableUsers(response.data);
    } catch (error) {
      console.error('Error fetching available users:', error);
      toast.error('Failed to fetch available users');
    }
  };

  const handleEnrollUser = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/enrollments/course/${courseId}`, enrollmentForm);
      toast.success('Learner enrolled successfully');
      setShowEnrollModal(false);
      setEnrollmentForm({
        user_id: '',
        enrollment_date: new Date().toISOString().split('T')[0],
        status: 1
      });
      fetchEnrollments();
    } catch (error) {
      console.error('Error enrolling user:', error);
      toast.error('Failed to enroll user: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleUpdateEnrollment = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/enrollments/${selectedEnrollment.id}`, {
        enrollment_date: enrollmentForm.enrollment_date,
        status: enrollmentForm.status
      });
      toast.success('Enrollment updated successfully');
      setShowEditModal(false);
      setSelectedEnrollment(null);
      fetchEnrollments();
    } catch (error) {
      console.error('Error updating enrollment:', error);
      toast.error('Failed to update enrollment: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleDeleteClick = (enrollment) => {
    setEnrollmentToDelete(enrollment);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!enrollmentToDelete) return;
    
    try {
      setDeleteLoading(true);
      await api.enrollments.delete(enrollmentToDelete.id);
      
      toast.success(`User "${getUserDisplayName(enrollmentToDelete.User)}" has been unenrolled.`);
      
      fetchEnrollments();
      setShowDeleteModal(false);
      setEnrollmentToDelete(null);
    } catch (error) {
      console.error('Error unenrolling user:', error);
      toast.error('Failed to unenroll user: ' + (error.response?.data?.message || error.message));
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setEnrollmentToDelete(null);
  };

  const handleRestoreEnrollment = async (enrollment) => {
    try {
      await api.enrollments.restore(enrollment.id);
      toast.success(`Enrollment for ${getUserDisplayName(enrollment.User)} restored successfully`);
      fetchEnrollments();
    } catch (error) {
      console.error('Error restoring enrollment:', error);
      toast.error('Failed to restore enrollment: ' + (error.response?.data?.message || error.message));
    }
  };

  const openEnrollModal = () => {
    if (!courseId) {
      toast.error('Course information not loaded yet. Please wait and try again.');
      return;
    }
    fetchAvailableUsers();
    setShowEnrollModal(true);
  };

  const openGroupEnrollModal = async () => {
    if (!courseId) {
      toast.error('Course information not loaded yet. Please wait and try again.');
      return;
    }
    setSelectedGroupId('');
    setGroupMembers({ assigned: [], skipped: [] });
    try {
      const response = await api.groups.list();
      const allGroups = Array.isArray(response.data?.data) ? response.data.data : [];
      // Only show groups not linked to any organization (platform-level groups)
      setPlatformGroups(allGroups.filter((g) => !g.organization_id));
    } catch (error) {
      toast.error('Failed to load groups: ' + (error.response?.data?.message || error.message));
    }
    setShowGroupEnrollModal(true);
  };

  const handleGroupSelect = async (e) => {
    const groupId = e.target.value;
    setSelectedGroupId(groupId);
    setGroupMembers({ assigned: [], skipped: [] });
    if (!groupId) return;

    try {
      setGroupMembersLoading(true);
      const response = await api.groups.getUsers(groupId);
      if (!response.data?.success) {
        toast.error('Failed to load group members.');
        return;
      }
      const { assigned: groupAssigned } = response.data.data;

      // Cross-reference with current enrollments to show preview
      const enrolledIds = new Set(
        enrollments.filter((en) => !en.deleted).map((en) => en.user_id)
      );
      const toEnroll = (groupAssigned || []).filter((u) => !enrolledIds.has(u.id));
      const alreadyIn = (groupAssigned || []).filter((u) => enrolledIds.has(u.id));
      setGroupMembers({ assigned: toEnroll, skipped: alreadyIn });
    } catch (error) {
      toast.error('Failed to load group members: ' + (error.response?.data?.message || error.message));
    } finally {
      setGroupMembersLoading(false);
    }
  };

  const handleGroupEnrollSave = async () => {
    if (!selectedGroupId) {
      toast.error('Please select a group first.');
      return;
    }
    if (groupMembers.assigned.length === 0) {
      toast.info('All members of this group are already enrolled.');
      return;
    }
    try {
      setGroupEnrollSaving(true);
      const response = await api.enrollments.enrollGroup(courseId, selectedGroupId);
      const { enrolled, skipped } = response.data;
      toast.success(`${enrolled} user(s) enrolled. ${skipped} already enrolled (skipped).`);
      setShowGroupEnrollModal(false);
      setSelectedGroupId('');
      setGroupMembers({ assigned: [], skipped: [] });
      fetchEnrollments();
    } catch (error) {
      toast.error('Failed to enroll group: ' + (error.response?.data?.message || error.message));
    } finally {
      setGroupEnrollSaving(false);
    }
  };

  const openEditModal = (enrollment) => {
    setSelectedEnrollment(enrollment);
    setEnrollmentForm({
      user_id: enrollment.user_id,
      enrollment_date: new Date(enrollment.enrollment_date).toISOString().split('T')[0],
      status: enrollment.status
    });
    setShowEditModal(true);
  };

  const getUserDisplayName = (user) => {
    if (!user) return 'Unknown User';
    return `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email || 'Unknown User';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleFilterChange = (e) => {
    setFilterStatus(e.target.value);
  };

  const filteredEnrollments = enrollments.filter(enrollment => {
    const searchFields = [
      getUserDisplayName(enrollment.User) || '',
      enrollment.User?.email || '',
      enrollment.User?.id?.toString() || ''
    ].join(' ').toLowerCase();
    
    const matchesSearch = !searchTerm || searchFields.includes(searchTerm.toLowerCase());
    
    let matchesFilter = true;
    switch(filterStatus) {
      case 'active':
        matchesFilter = enrollment.status === 1 && !enrollment.deleted;
        break;
      case 'inactive':
        matchesFilter = enrollment.status === 0 && !enrollment.deleted;
        break;
      case 'deleted':
        matchesFilter = enrollment.deleted;
        break;
      default:
        matchesFilter = !enrollment.deleted;
        break;
    }
    
    return matchesSearch && matchesFilter;
  });

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center min-h-screen">
        <Spinner animation="border" role="status" className="w-12 h-12 text-purple-600">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-4 md:p-6 bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 min-h-screen">
      {/* Header */}
      <div className="mb-4">
        <Row className="align-items-center g-3">
          <Col md={2}>
            <Button 
              bsPrefix="custom-hamburger"
              onClick={() => navigate('/admin/courses')}
              className="w-full border border-purple-300 bg-white text-purple-600 hover:bg-purple-50 font-semibold rounded-lg shadow-md hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-2 text-sm py-2"
            >
              <FaArrowLeft className="w-5 h-5" /> Back
            </Button>
          </Col>
          <Col md={10}>
            <h5 className="fw-bold mb-0 text-xl sm:text-2xl md:text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">
              Enrollments: {course ? (course.title || course.name) : 'Loading...'}
            </h5>
          </Col>
        </Row>
      </div>

      {/* Search + Filter + Action */}
      <Row className="mb-4 g-3 align-items-center">
        <Col xs={12} md={4}>
          <InputGroup className="shadow-sm rounded-lg sm:rounded-xl overflow-hidden">
            <InputGroup.Text className="bg-white border-0 text-purple-600 px-3">
              <FaSearch className="w-5 h-5" />
            </InputGroup.Text>
            <Form.Control
              placeholder="Search by name, email, or ID..."
              value={searchTerm}
              onChange={handleSearch}
              className="border-0 focus:ring-2 focus:ring-purple-500 text-sm"
            />
          </InputGroup>
        </Col>
        <Col xs={12} sm={6} md={2}>
          <Form.Select 
            value={filterStatus} 
            onChange={handleFilterChange}
            className="rounded-lg sm:rounded-xl shadow-sm border-purple-200 focus:ring-2 focus:ring-purple-500 text-sm"
          >
            <option value="all">All Enrollments</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="deleted">Deleted</option>
          </Form.Select>
        </Col>
        <Col xs={12} sm={6} md={2}>
          <Button 
            bsPrefix="custom-hamburger"
            onClick={openEnrollModal}
            className="w-full border border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-2 text-sm h-full p-2"
          >
            <FaPlus className="w-5 h-5" />
            <span className="whitespace-nowrap">Enroll Learner</span>
          </Button>
        </Col>
        <Col xs={12} sm={6} md={2}>
          <Button 
            bsPrefix="custom-hamburger"
            onClick={openGroupEnrollModal}
            className="w-full border border-blue-500 bg-white text-blue-600 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-2 text-sm h-full p-2"
          >
            <FaLayerGroup className="w-5 h-5" />
            <span className="whitespace-nowrap">Enroll by Group</span>
          </Button>
        </Col>
      </Row>

      {/* No Data */}
      {filteredEnrollments.length === 0 ? (
        <Alert variant="info" className="text-center py-8 rounded-xl sm:rounded-2xl bg-white/80 backdrop-blur shadow-md border border-purple-100">
          <p className="text-sm sm:text-base font-medium text-purple-700">
            {searchTerm || filterStatus !== 'all' 
              ? 'No enrollments found matching your criteria.' 
              : 'No learners enrolled yet. Click "Enroll Learner" to add the first one.'
            }
          </p>
        </Alert>
      ) : (
        <div className="overflow-x-auto -mx-3 sm:mx-0 rounded-xl sm:rounded-2xl shadow-lg bg-white">
          <Table bordered hover className="m-0 min-w-max text-xs sm:text-sm">
            <thead className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
              <tr>
                <th className="py-3 px-3 text-left font-bold">#</th>
                <th className="py-3 px-3 text-left font-bold">User Name</th>
                <th className="py-3 px-3 text-left font-bold">Email</th>
                <th className="py-3 px-3 text-left font-bold">Enrolled On</th>
                <th className="py-3 px-3 text-left font-bold">Status</th>
                <th className="py-3 px-3 text-left font-bold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredEnrollments.map((enrollment, index) => (
                <tr key={enrollment.id} className={`border-b border-purple-100 hover:bg-purple-50/50 transition-all ${enrollment.deleted ? 'bg-red-50' : ''}`}>
                  <td className="py-3 px-4 text-gray-600">{index + 1}</td>
                  <td className="py-3 px-4">
                    <div className="d-flex align-items-center gap-3">
                 
                      <div>
                        <div className="font-semibold text-gray-800">{getUserDisplayName(enrollment.User)}</div>
                        {enrollment.deleted && <Badge bg="danger" className="text-xs">Deleted</Badge>}
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-gray-600 break-all">{enrollment.User?.email || 'N/A'}</td>
                  <td className="py-3 px-4 text-left text-gray-600">{formatDate(enrollment.enrollment_date)}</td>
                  <td className="py-3 px-4 text-left">
                    <Badge bg={enrollment.status === 1 ? 'success' : 'danger'} className="px-3 py-1 text-xs">
                      {enrollment.status === 1 ? 'Active' : 'Inactive'}
                    </Badge>
                  </td>
                  <td className="py-3 px-4">
                    <div className="d-flex gap-2 justify-content-left">
                      <Button
                        bsPrefix="custom-hamburger"
                        size="sm"
                        onClick={() => openEditModal(enrollment)}
                        title="Edit Enrollment"
                        disabled={enrollment.deleted}
                        className="border-purple-300 text-purple-600 hover:bg-purple-50 rounded p-1 disabled:opacity-50"
                      >
                        <FaEdit className="w-5 h-5" />
                      </Button>
                      {!enrollment.deleted ? (
                        <Button
                          bsPrefix="custom-hamburger"
                          size="sm"
                          onClick={() => handleDeleteClick(enrollment)}
                          title="Unenroll"
                          className="border-red-300 text-red-600 hover:bg-red-50 rounded p-1"
                        >
                          <FaTrash className="w-5 h-5" />
                        </Button>
                      ) : (
                        <Button
                          bsPrefix="custom-hamburger"
                          size="sm"
                          onClick={() => handleRestoreEnrollment(enrollment)}
                          title="Restore"
                          className="border-green-300 text-green-600 hover:bg-green-50 rounded p-1"
                        >
                          <FaCheck className="w-5 h-5" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      {/* Enroll Modal */}
      <Modal show={showEnrollModal} onHide={() => setShowEnrollModal(false)} size="md" fullscreen="sm-down" centered backdrop="static">
        <Modal.Header closeButton closeVariant="white" className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 py-4 px-5">
          <Modal.Title className="font-bold text-lg">
             Enroll Learner
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleEnrollUser}>
          <Modal.Body className="p-0 sm:p-5 bg-gray-50">
            <Row className="g-4">
              <Col xs={12}>
                <Form.Group>
                  <Form.Label className="font-semibold text-gray-700">Select Learner <span className="text-danger">*</span></Form.Label>
                  <Form.Select
                    value={enrollmentForm.user_id}
                    onChange={(e) => setEnrollmentForm({...enrollmentForm, user_id: e.target.value})}
                    required
                    className="rounded-lg border-purple-200 focus:ring-purple-500"
                  >
                    <option value="">Choose a learner...</option>
                    {availableUsers.map(user => (
                      <option key={user.id} value={user.id}>
                        {getUserDisplayName(user)} ({user.email})
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="font-semibold text-gray-700">Enrollment Date <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="date"
                    value={enrollmentForm.enrollment_date}
                    onChange={(e) => setEnrollmentForm({...enrollmentForm, enrollment_date: e.target.value})}
                    required
                    className="rounded-lg border-purple-200 focus:ring-purple-500"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="font-semibold text-gray-700">Status <span className="text-danger">*</span></Form.Label>
                  <Form.Select
                    value={enrollmentForm.status}
                    onChange={(e) => setEnrollmentForm({...enrollmentForm, status: parseInt(e.target.value)})}
                    required
                    className="rounded-lg border-purple-200 focus:ring-purple-500"
                  >
                    <option value={1}>Active</option>
                    <option value={0}>Inactive</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer className="bg-gray-100 border-0 p-0">
            <Row className="w-100 g-3">
              <Col xs={6}>
                <Button bsPrefix="custom-hamburger" onClick={() => setShowEnrollModal(false)} className="w-full border-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-gray-300 hover:border-gray-500 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2">
                  Cancel
                </Button>
              </Col>
              <Col xs={6}>
                <Button bsPrefix="custom-hamburger" type="submit" className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2">
                  Enroll Learner
                </Button>
              </Col>
            </Row>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Edit Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="md" fullscreen="sm-down" centered>
        <Modal.Header closeButton closeVariant="white" className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 py-3 px-4">
          <Modal.Title className="font-bold text-lg">
            Edit Enrollment
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleUpdateEnrollment}>
          <Modal.Body className="p-0 sm:p-6 bg-gray-50">
            {selectedEnrollment && (
              <Alert variant="info" className="mb-4 rounded-lg border-purple-200 bg-purple-50 text-purple-800">
                <strong>User:</strong> {getUserDisplayName(selectedEnrollment.User)} ({selectedEnrollment.User?.email})
              </Alert>
            )}
            <Row className="g-4">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="font-semibold text-gray-700">Enrollment Date <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="date"
                    value={enrollmentForm.enrollment_date}
                    onChange={(e) => setEnrollmentForm({...enrollmentForm, enrollment_date: e.target.value})}
                    required
                    className="rounded-lg border-purple-200 focus:ring-purple-500"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="font-semibold text-gray-700">Status <span className="text-danger">*</span></Form.Label>
                  <Form.Select
                    value={enrollmentForm.status}
                    onChange={(e) => setEnrollmentForm({...enrollmentForm, status: parseInt(e.target.value)})}
                    required
                    className="rounded-lg border-purple-200 focus:ring-purple-500"
                  >
                    <option value={1}>Active</option>
                    <option value={0}>Inactive</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer className="bg-gray-100 border-0 p-0">
            <div class="w-100 g-2 row">
              <div class="col-6">
            <Button bsPrefix="custom-hamburger" onClick={() => setShowEditModal(false)} className="w-full border-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-gray-300 hover:border-gray-500 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2">
              Cancel
            </Button>
            </div>
            <div class="col-6">
            <Button bsPrefix="custom-hamburger" type="submit" className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2 ">
              Update Enrollment
            </Button>
            </div>
            </div>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Enroll by Group Modal */}
      <Modal show={showGroupEnrollModal} onHide={() => !groupEnrollSaving && setShowGroupEnrollModal(false)} size="lg" centered backdrop="static">
        <Modal.Header closeButton closeVariant="white" className="bg-gradient-to-r from-blue-500 to-purple-600 text-white border-0 py-3 px-4 modal-header">
          <Modal.Title className="font-bold text-lg text-white">
            <FaLayerGroup className="inline me-2" /> Enroll by Group
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-gray-50 p-4">
          <Form.Group className="mb-4">
            <Form.Label className="font-semibold text-gray-700 text-sm">
              Select Group <span className="text-danger">*</span>
            </Form.Label>
            <Form.Select
              value={selectedGroupId}
              onChange={handleGroupSelect}
              disabled={groupMembersLoading || groupEnrollSaving}
              className="rounded-lg border-purple-200 focus:ring-purple-500 text-sm"
            >
              <option value="">Choose a platform group...</option>
              {platformGroups.map((g) => (
                <option key={g.id} value={g.id}>{g.name}</option>
              ))}
            </Form.Select>
            {platformGroups.length === 0 && (
              <Form.Text className="text-muted text-xs">No platform-level groups found (groups must not be linked to any organization).</Form.Text>
            )}
          </Form.Group>

          {groupMembersLoading ? (
            <div className="text-center py-8">
              <Spinner animation="border" className="text-purple-600" />
            </div>
          ) : selectedGroupId ? (
            <div className="rounded-xl overflow-hidden border border-purple-100">
              {/* Will be enrolled */}
              <div className="px-3 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm font-semibold flex items-center justify-between">
                <span><FaCheck className="inline me-1" /> Will be enrolled</span>
                <Badge bg="light" text="dark">{groupMembers.assigned.length}</Badge>
              </div>
              <div className="bg-white px-3 py-2 max-h-40 overflow-y-auto">
                {groupMembers.assigned.length === 0 ? (
                  <p className="text-gray-400 text-xs py-2">All group members are already enrolled.</p>
                ) : (
                  groupMembers.assigned.map((u) => (
                    <div key={u.id} className="flex items-center gap-2 py-1 border-b border-gray-50 last:border-0">
                      <FaCheck className="text-green-500 shrink-0 w-3 h-3" />
                      <span className="text-sm text-gray-800">{getUserDisplayName(u)}</span>
                      <span className="text-xs text-gray-500">({u.email})</span>
                    </div>
                  ))
                )}
              </div>
              {/* Already enrolled — skipped */}
              {groupMembers.skipped.length > 0 && (
                <>
                  <div className="px-3 py-2 bg-gray-200 text-gray-600 text-sm font-semibold flex items-center justify-between">
                    <span><FaTimes className="inline me-1" /> Already enrolled (skipped)</span>
                    <Badge bg="secondary">{groupMembers.skipped.length}</Badge>
                  </div>
                  <div className="bg-gray-50 px-3 py-2 max-h-32 overflow-y-auto">
                    {groupMembers.skipped.map((u) => (
                      <div key={u.id} className="flex items-center gap-2 py-1 border-b border-gray-100 last:border-0">
                        <FaTimes className="text-gray-400 shrink-0 w-3 h-3" />
                        <span className="text-sm text-gray-500 line-through">{getUserDisplayName(u)}</span>
                        <span className="text-xs text-gray-400">({u.email})</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          ) : null}
        </Modal.Body>
        <Modal.Footer className="bg-gray-100 border-0 p-3">
          <Row className="w-100 g-3">
            <Col xs={6}>
              <Button
                bsPrefix="custom-hamburger"
                onClick={() => setShowGroupEnrollModal(false)}
                disabled={groupEnrollSaving}
                className="w-full border-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-gray-300 hover:border-gray-500 font-semibold rounded-lg shadow-md transition-all duration-200 text-sm flex items-center justify-center gap-2 p-2"
              >
                Cancel
              </Button>
            </Col>
            <Col xs={6}>
              <Button
                bsPrefix="custom-hamburger"
                onClick={handleGroupEnrollSave}
                disabled={groupEnrollSaving || !selectedGroupId || groupMembers.assigned.length === 0}
                className="w-full border-1 border-purple-600 bg-white text-purple-600 font-semibold rounded-lg shadow-md hover:shadow-lg transition-all duration-200 text-sm flex items-center justify-center gap-2 p-2 disabled:opacity-50"
              >
                {groupEnrollSaving ? (
                  <><Spinner animation="border" size="sm" className="me-2" /> Enrolling...</>
                ) : (
                  <><FaUsers className="w-4 h-4" /> Enroll {groupMembers.assigned.length} User(s)</>
                )}
              </Button>
            </Col>
          </Row>
        </Modal.Footer>
      </Modal>

      {/* Delete Modal */}
      <Modal show={showDeleteModal} onHide={handleDeleteCancel} centered>
        <Modal.Header closeButton closeVariant="white" className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 py-3 px-4 sm:px-4 sm:py-4 modal-header">
          <Modal.Title className="font-bold text-xl text-white">
            Confirm Unenroll
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-10 bg-gray-50">
          <div className="mb-4">
            <FaTrash size={60} className="text-red-500 mx-auto" />
          </div>
          <h5 className="font-bold text-gray-800 mb-3">Unenroll User</h5>
          <p className="text-gray-600">
            Are you sure you want to unenroll{' '}
            <strong>{enrollmentToDelete ? getUserDisplayName(enrollmentToDelete.User) : ''}</strong> from this course?
          </p>
          <p className="text-sm text-gray-500">This is a soft delete. The enrollment can be restored later.</p>
        </Modal.Body>
        <Modal.Footer className="bg-gray-100 border-0 rounded-b-2xl p-4">
          <Row className="w-100 g-3">
            <Col md={6}>
              <Button 
                bsPrefix="custom-hamburger" 
                onClick={handleDeleteCancel}
                disabled={deleteLoading}
                className="w-full border-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-gray-300 hover:border-gray-500 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
              >
                Cancel
              </Button>
            </Col>
            <Col md={6}>
              <Button 
                bsPrefix="custom-hamburger"
                onClick={handleDeleteConfirm}
                disabled={deleteLoading}
                className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
              >
                {deleteLoading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Unenrolling...
                  </>
                ) : (
                  'Unenroll User'
                )}
              </Button>
            </Col>
          </Row>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default CourseEnrollmentsPage;